import { jsx, jsxs } from "react/jsx-runtime";
import MButton from "@mui/material/Button";
import styled from "@mui/material/styles/styled";
import useMediaQuery from "@mui/material/useMediaQuery";
import cn from "classnames";
import isFunction from "lodash/isFunction";
import isObject from "lodash/isObject";
import isUndefined from "lodash/isUndefined";
import React, { useState } from "react";
import { useParentBg } from "../../shared/hooks/current-color";
import { EDionButtonSizes } from "../../tokens";
import { Icon } from "../Icon";
import IconButton from "../IconButton";
import Spinner from "../Spinner";
import { useTheme } from "../ThemeProvider";
var EButtonPseudo = /* @__PURE__ */ ((EButtonPseudo2) => {
  EButtonPseudo2["HOVER"] = "&:hover::before";
  EButtonPseudo2["ACTIVE"] = "&:active::before";
  EButtonPseudo2["CLEAR"] = "not($disabled):not($error):not($focused)";
  return EButtonPseudo2;
})(EButtonPseudo || {});
var EButtonVariants = /* @__PURE__ */ ((EButtonVariants2) => {
  EButtonVariants2["PRIMARY"] = "primary";
  EButtonVariants2["SECONDARY"] = "secondary";
  EButtonVariants2["GHOST"] = "ghost";
  EButtonVariants2["TEXT"] = "text";
  EButtonVariants2["COLOR"] = "color";
  return EButtonVariants2;
})(EButtonVariants || {});
var EButtonColors = /* @__PURE__ */ ((EButtonColors2) => {
  EButtonColors2["ERROR"] = "error";
  EButtonColors2["INFO"] = "info";
  EButtonColors2["SUCCESS"] = "success";
  EButtonColors2["WARNING"] = "warning";
  EButtonColors2["ELEVATION_0"] = "elevation0";
  EButtonColors2["ELEVATION_1"] = "elevation1";
  EButtonColors2["ELEVATION_2"] = "elevation2";
  EButtonColors2["ELEVATION_3"] = "elevation3";
  EButtonColors2["ON_BRAND"] = "onBrand";
  EButtonColors2["BRAND"] = "brand";
  EButtonColors2["BLUE_ELEVATION_1"] = "blueElevation1";
  EButtonColors2["BLUE_ELEVATION_2"] = "blueElevation2";
  EButtonColors2["SPECIAL"] = "special";
  EButtonColors2["STATUS1"] = "status1";
  EButtonColors2["STATUS2"] = "status2";
  EButtonColors2["STATUS3"] = "status3";
  EButtonColors2["STATUS4"] = "status4";
  EButtonColors2["STATUS5"] = "status5";
  return EButtonColors2;
})(EButtonColors || {});
const BUTTON_VARIANTS = Object.values(EButtonVariants);
const DEFAULT_VARIANT_BG = {
  ["primary" /* PRIMARY */]: "brand" /* BRAND */,
  ["secondary" /* SECONDARY */]: "elevation0" /* ELEVATION_0 */,
  ["ghost" /* GHOST */]: "elevation0" /* ELEVATION_0 */,
  ["text" /* TEXT */]: "elevation0" /* ELEVATION_0 */,
  ["color" /* COLOR */]: "success" /* SUCCESS */
};
const BRAND_COLORS = ["brand" /* BRAND */, "blueElevation1" /* BLUE_ELEVATION_1 */, "blueElevation2" /* BLUE_ELEVATION_2 */, "special" /* SPECIAL */];
const ELEVATION_COLORS = ["elevation0" /* ELEVATION_0 */, "elevation1" /* ELEVATION_1 */, "elevation2" /* ELEVATION_2 */, "elevation3" /* ELEVATION_3 */];
const STATUS_COLORS = [
  "success" /* SUCCESS */,
  "info" /* INFO */,
  "warning" /* WARNING */,
  "error" /* ERROR */,
  "status1" /* STATUS1 */,
  "status2" /* STATUS2 */,
  "status3" /* STATUS3 */,
  "status4" /* STATUS4 */,
  "status5" /* STATUS5 */
];
const VARIANT_COLORS = {
  ["primary" /* PRIMARY */]: ["brand" /* BRAND */, "onBrand" /* ON_BRAND */],
  ["secondary" /* SECONDARY */]: [...ELEVATION_COLORS, ...STATUS_COLORS],
  ["ghost" /* GHOST */]: [...ELEVATION_COLORS, ...STATUS_COLORS],
  ["text" /* TEXT */]: [...ELEVATION_COLORS, ...STATUS_COLORS],
  ["color" /* COLOR */]: [...ELEVATION_COLORS, ...STATUS_COLORS]
};
const NO_ICON_MARGINS_SX = {
  "& .MuiButton-startIcon": {
    marginLeft: "0px",
    marginRight: "0px",
    transition: "none"
  }
};
const LOADING_SX = {
  // cursor: 'not-allowed',
  // pointerEvents: 'none',
  color: "transparent",
  pointerEvents: "unset",
  cursor: "not-allowed",
  // backgroundColor: 'inherit',
  "&:hover": {
    color: "transparent",
    pointerEvents: "unset",
    cursor: "not-allowed"
    // backgroundColor: 'inherit',
  }
};
const CONTENT_STYLES = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};
const NO_ELLIPSIS_SX = {
  overflow: "hidden",
  textOverflow: "none"
};
const isNodeIterable = (obj) => isObject(obj) && obj[Symbol.iterator] !== void 0;
const children2Array = (children) => {
  if (!children)
    return [];
  return isNodeIterable(children) ? Array.from(children) : [children];
};
const areChildrenEmpty = (children) => children2Array(children).every(isUndefined);
const isValidColor = (color, variant) => color && variant && VARIANT_COLORS[variant]?.includes(color) || false;
const getActionEffectSX = (palette, variant, color, pseudoClass, disabled = false, noHover = false) => {
  if (variant === "text" /* TEXT */)
    return;
  const action = pseudoClass === "&:active::before" /* ACTIVE */ ? "press" : "hover";
  const result = {
    "&::before": {
      content: '""',
      position: "absolute",
      height: "100%",
      width: "100%",
      top: 0,
      left: 0,
      borderRadius: "inherit",
      visibility: "hidden",
      opacity: 0,
      transition: "all 0.25s ease"
    }
  };
  if (action === "press") {
    result[pseudoClass] = {
      backgroundColor: noHover ? "transparent" : `${palette[color][action]} !important`,
      visibility: disabled ? "hidden" : "visible",
      opacity: 1
    };
  }
  if (action === "hover") {
    result["@media (hover: hover) and (pointer: fine)"] = {
      [pseudoClass]: {
        backgroundColor: noHover ? "transparent" : `${palette[color][action]} !important`,
        visibility: disabled ? "hidden" : "visible",
        opacity: 1
      }
    };
  }
  return result;
};
const StyledButton = styled(MButton)(
  (props) => {
    const {
      theme,
      caps,
      color: propColor,
      variant = "primary" /* PRIMARY */,
      size,
      loading,
      onBrand,
      disabled,
      selected,
      fullWidth,
      originalChildren: children,
      deprecatedSX,
      noHover,
      noEllipsis
    } = props;
    const parentBg = useParentBg();
    const isMissingVariant = !BUTTON_VARIANTS.includes(variant);
    const isPrimary = variant === "primary" /* PRIMARY */;
    const isSecondary = variant === "secondary" /* SECONDARY */;
    const isText = variant === "text" /* TEXT */;
    const isGhost = variant === "ghost" /* GHOST */;
    const isColor = variant === "color" /* COLOR */;
    const hasColor = isSecondary || isGhost || isColor || isText;
    const isBrandBg = BRAND_COLORS.includes(parentBg);
    const isOnBrand = isPrimary && (onBrand || isBrandBg);
    const bg = isOnBrand ? "onBrand" : hasColor ? propColor ?? parentBg : DEFAULT_VARIANT_BG[variant];
    const isGhostMainColor = isGhost && STATUS_COLORS.includes(bg);
    const isTextMainColor = isText && STATUS_COLORS.includes(bg);
    const isSecondaryMainColor = isSecondary && STATUS_COLORS.includes(bg);
    const isNonPrimaryOnBrand = isBrandBg && (isSecondary || isGhost);
    const palette = theme.palette;
    const typographySize = theme.button.typography[isText ? "text" : "nonText"][size];
    const buttonSize = isText ? theme.typography[typographySize].fontSize : theme.button.size[size];
    const hasStartIcon = !!props.startIcon;
    const hasEndIcon = !!props.endIcon;
    const hasContent = !areChildrenEmpty(children);
    const contentLeftMargin = hasStartIcon || isText ? 0 : theme.button.spacing.horizontal.outer[size];
    const contentRightMargin = hasEndIcon || isText ? 0 : theme.button.spacing.horizontal.outer[size];
    const startIconLeftMargin = hasStartIcon && hasContent ? theme.button.spacing.horizontal.outer[size] : 0;
    const startIconRightMargin = hasStartIcon && hasContent ? theme.button.spacing.horizontal.inner[isText ? "text" : "nonText"][size] : 0;
    const endIconLeftMargin = hasEndIcon && hasContent ? theme.button.spacing.horizontal.inner[isText ? "text" : "nonText"][size] : 0;
    const endIconRightMargin = hasEndIcon && hasContent ? theme.button.spacing.horizontal.outer[size] : 0;
    const typographyParams = theme.typography[typographySize];
    const bRadius = theme.button.borderRadius[size];
    const hoverPressSX = !loading && {
      ...getActionEffectSX(palette, variant, bg, "&:hover::before" /* HOVER */, disabled, noHover || disabled || selected),
      ...getActionEffectSX(palette, variant, bg, "&:active::before" /* ACTIVE */, disabled, noHover || disabled || selected)
    };
    const forceFullWidth = hasContent && fullWidth;
    const disabledText = isNonPrimaryOnBrand ? palette[bg].hover : palette[bg].disableText;
    let compBackground;
    let compColor = palette[bg].contrastText;
    if (isGhost || isText)
      compColor = palette[bg].contrast;
    if (isGhostMainColor || isSecondaryMainColor || isTextMainColor)
      compColor = palette[bg].mainContent ?? palette[bg].main;
    if (isPrimary || isMissingVariant) {
      compBackground = isOnBrand ? palette.onBrand.main : palette.brand.main;
      compColor = isOnBrand ? palette.onBrand.contrastText : palette.brand.contrastText;
    }
    if (isSecondary)
      compBackground = palette[bg].transparent;
    if (isColor)
      compBackground = palette[bg].main;
    if (isGhost)
      compBackground = "transparent";
    if (isNonPrimaryOnBrand)
      compColor = palette[bg].mainContent ?? palette[bg].contrastText;
    if (selected || isText && !props.color) {
      compColor = palette[bg].accent;
    }
    if (disabled) {
      if (!isText)
        compBackground = isOnBrand ? palette.brand.transparent : palette[propColor ?? bg]?.disable ?? palette[bg].disable;
      if (isGhost)
        compBackground = "transparent";
      compColor = disabledText;
    }
    const extraSettings = hasContent ? {} : {
      maxWidth: buttonSize,
      maxHeight: buttonSize,
      minWidth: buttonSize,
      minHeight: buttonSize,
      ...NO_ICON_MARGINS_SX
    };
    const loadingSX = loading && {
      ...LOADING_SX,
      color: "transparent !important",
      backgroundColor: `${compBackground} !important`,
      "&:hover": {
        ...LOADING_SX["&:hover"],
        backgroundColor: `${compBackground} !important`
      },
      // покрасим спиннер в цвет контента
      "& div > span.MuiCircularProgress-root": {
        color: `${compColor} !important`
      }
    } || {};
    const result = {
      ...deprecatedSX,
      ...typographyParams,
      padding: 0,
      // paddingRight: paddingSize,
      // paddingLeft: paddingSize,
      color: `${compColor}`,
      height: buttonSize,
      borderRadius: bRadius,
      minWidth: "0px",
      backgroundColor: compBackground,
      boxShadow: "none !important",
      whiteSpace: "nowrap",
      cursor: noHover || selected ? "default" : "pointer",
      "&:hover": {
        backgroundColor: `${compBackground} !important`,
        color: `${compColor}`
      },
      ...hoverPressSX,
      "& div > svg": { color: compColor },
      "& div > div > svg": { color: compColor },
      ...loadingSX,
      "& .MuiButton-startIcon": {
        transition: "none",
        marginLeft: `${startIconLeftMargin}px`,
        marginRight: `${startIconRightMargin}px`
      },
      "& .MuiButton-endIcon": {
        marginLeft: `${endIconLeftMargin}px`,
        marginRight: `${endIconRightMargin}px`
      },
      "& .button-content": {
        marginLeft: `${contentLeftMargin}px`,
        marginRight: `${contentRightMargin}px`,
        ...noEllipsis ? NO_ELLIPSIS_SX : CONTENT_STYLES
      },
      ...extraSettings,
      "&.Mui-disabled": {
        pointerEvents: "unset",
        cursor: "not-allowed",
        color: disabledText,
        background: compBackground,
        "&:hover": {
          color: disabledText,
          background: compBackground,
          cursor: "not-allowed",
          "&::before": {
            display: "none"
          }
        }
      },
      // transition: 'ease-in-out 0.2s',
      width: forceFullWidth ? "100%" : "fit-content"
    };
    if (isText && caps) {
      result.textTransform = "uppercase";
    }
    return result;
  }
);
const Button = React.forwardRef((props, ref) => {
  const {
    size = EDionButtonSizes.M,
    startIcon,
    endIcon,
    iconSize: _iconSize,
    children,
    loading,
    onEndIconClick,
    deprecatedSX,
    className,
    ...buttonProps
  } = props;
  const extraProps = {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [contentRef, setContentRef] = useState();
  const [showTooltip, setShowTooltip] = useState(false);
  const isText = buttonProps.variant === "text" /* TEXT */;
  const iconSize = _iconSize ?? theme.button.icon[isText ? "text" : "nonText"][size];
  const classes = cn(className, `button-size-${size}`);
  const handleClickIcon = React.useCallback((e) => {
    e.stopPropagation();
    if (isFunction(onEndIconClick))
      onEndIconClick();
  }, [onEndIconClick]);
  if (startIcon)
    extraProps.startIcon = /* @__PURE__ */ jsx(Icon, { icon: startIcon, size: iconSize, invisible: loading });
  if (endIcon)
    extraProps.endIcon = onEndIconClick && !loading ? /* @__PURE__ */ jsx(IconButton, { onClick: handleClickIcon, color: "inherit", children: /* @__PURE__ */ jsx(Icon, { icon: endIcon, size: iconSize, invisible: loading }) }) : /* @__PURE__ */ jsx(Icon, { icon: endIcon, size: iconSize, invisible: loading });
  return (
    // <Tooltip title={ props.children } enterDelay={ 100 } leaveDelay={ 200 } disableHoverListener={ !showTooltip } disableFocusListener={ !showTooltip } >
    /* @__PURE__ */ jsxs(
      StyledButton,
      {
        size,
        loading,
        ...buttonProps,
        ...extraProps,
        originalChildren: children,
        isMobile,
        deprecatedSX,
        ref,
        className: classes,
        children: [
          loading && /* @__PURE__ */ jsx(Spinner, { size: iconSize }),
          props.children && /* @__PURE__ */ jsx("span", { className: "button-content", ref: setContentRef, children: props.children })
        ]
      }
    )
  );
});
var Button_default = Button;
export {
  EButtonColors,
  EDionButtonSizes as EButtonSize,
  EButtonVariants,
  Button_default as default
};
